import { useEffect, useRef, useState } from 'react';
import { useInView } from '../hooks/useApi.js';

/** Best-effort numeric extraction so "54" → 54, "2k+" → 2000, "98%" → 98. */
function parseNumeric(raw) {
  if (raw === null || raw === undefined) return null;
  const s = String(raw).trim().toLowerCase();
  // "2k", "2k+", "1.5k"
  const km = s.match(/^([\d.]+)\s*k/);
  if (km) return Math.round(parseFloat(km[1]) * 1000);
  const m = s.match(/^[\d.]+/);
  return m ? parseFloat(m[0]) : null;
}

/** Re-format `n` into the same shape as the original target ("2k+", "98%"). */
function formatLike(target, n) {
  const s = String(target);
  if (/k\+?/i.test(s)) {
    const suffix = /\+/.test(s) ? 'k+' : 'k';
    return `${Math.round(n / 1000)}${suffix}`;
  }
  if (/%/.test(s)) return `${Math.round(n)}%`;
  if (/\+/.test(s)) return `${Math.round(n)}+`;
  return Math.round(n).toString();
}

function CountUp({ to, target, durationMs = 1500, start = false }) {
  const [value, setValue] = useState(start ? to : 0);
  const startedAt = useRef(null);

  useEffect(() => {
    if (!start || to === null) return;
    let raf;
    const step = (ts) => {
      if (!startedAt.current) startedAt.current = ts;
      const elapsed = ts - startedAt.current;
      const t = Math.min(1, elapsed / durationMs);
      // ease-out cubic
      const eased = 1 - Math.pow(1 - t, 3);
      setValue(to * eased);
      if (t < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [start, to, durationMs]);

  return <>{formatLike(target, value)}</>;
}

export function StatsBar({ stats }) {
  const items = stats && stats.length ? stats : [
    { id: 'f1', number: '54', suffix: 'yrs', label: 'Of Legacy' },
    { id: 'f2', number: '98%', suffix: '', label: 'University Placement' },
    { id: 'f3', number: '2k+', suffix: '', label: 'Students Enrolled' },
    { id: 'f4', number: '120+', suffix: '', label: 'Faculty Members' },
  ];

  const [ref, inView] = useInView({ threshold: 0.3, once: true });

  return (
    <div className="stats-bar" ref={ref}>
      <div className="container">
        {items.map((s) => {
          const n = parseNumeric(s.number);
          return (
            <div className="stat" key={s.id}>
              <div className="num">
                {n === null
                  ? s.number
                  : <CountUp to={n} target={s.number} start={inView} />}
                {s.suffix && <em>{s.suffix}</em>}
              </div>
              <div className="lbl">{s.label}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
